import { rem } from 'polished';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Namespace } from 'i18next';
import styled, { css } from 'styled-components';
import { OpenExternalLink } from 'icons';
import { BodyText } from '../../Shared/Typography';
import { small, medium, large, getChannel } from '../../utils';
import tracking from '../../App/Tracking/tracking';
import PanelIllustration from './Assets/panel-illustration.svg';

const EmailClientLinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${small(css`
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-start;
  `)};
`;

const OpenExternalLinkIcon = styled(OpenExternalLink).attrs(() => ({
  primaryColor: 'currentColor',
}))`
  margin-right: 2px;
`;

const EmailClientLink = styled(BodyText).attrs(({ theme }) => ({
  forwardedAs: 'a',
  sizes: [theme.typography.fontSizes.fontSize14],
  weight: theme.typography.fontWeights.medium,
}))`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 16px;
  text-decoration: none;
  height: 34px;
  width: 142px;
  margin-right: 8px;
  margin-top: ${rem(12)};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  ${small(css`
    margin-top: ${rem(0)};
  `)};
`;

const EmailConfirmationRightPanel = styled.div`
  flex: 1;
  position: relative;
`;

const EmailConfirmationPanelIllustration = styled.img`
  position: absolute;
  bottom: 0;
  right: 0;
  max-width: ${rem(520)};

  ${large(css`
    max-width: ${rem(630)};
  `)}
`;

export const EmailConfirmationHelperText = styled(BodyText).attrs(
  ({ theme }) => ({
    forwardedAs: 'p',
    sizes: [theme.typography.fontSizes.fontSize18],
  }),
)`
  margin-top: -${rem(8)};
  ${medium(css`
    margin-top: -${rem(16)};
  `)}
`;

export const EmailConfirmationRightBlock = () => (
  <EmailConfirmationRightPanel>
    <EmailConfirmationPanelIllustration src={PanelIllustration} alt="" />
  </EmailConfirmationRightPanel>
);

// `u/0` is the account *index*, and is deliberately not the user's address.
// Gmail's `/mail/u/` segment returns "Temporary Error (404)" when given an
// address — verified even while signed into that exact account, which is why
// scoping the link to the recipient was reverted (ENG-21800, PR #3661). The
// index form cannot error: it opens the default signed-in account, or Google's
// sign-in page when signed out.
const GMAIL_SEARCH_URL = 'https://mail.google.com/mail/u/0/#search';

const GMAIL_SEARCH_TERMS = ['in:anywhere', 'newer_than:1d'];

/**
 * Deep link into Gmail, pre-filtered to the verification email, so users don't
 * have to hunt for it.
 *
 * Terms are encoded individually and joined. The previous implementation
 * pre-encoded `from:@` and then appended a full sender address, producing
 * `from:@support@unmind.com` — two `@`s, matching nothing. That failed silently
 * with an empty result set rather than erroring, which is how it survived
 * unnoticed since ENG-9963.
 *
 * The sender is per-channel, so it comes from channel config rather than a
 * hardcoded address, and is omitted entirely for channels whose sender we
 * haven't confirmed. See `Channel.senderDomain` — which also explains why this
 * search returns nothing on UAT and dev, where the sender differs from prod.
 */
export const generateGmailSniperLink = () => {
  const { senderDomain } = getChannel();

  const searchQuery = [
    ...(senderDomain ? [`from:${senderDomain}`] : []),
    ...GMAIL_SEARCH_TERMS,
  ]
    .map(encodeURIComponent)
    .join('+');

  return `${GMAIL_SEARCH_URL}/${searchQuery}`;
};

export const EmailConfirmationFooter = () => {
  const { t: translate } = useTranslation<Namespace<'shared'>>('shared');
  const trackEmailCTAClicked = () => tracking.track('open-email-cta-clicked');

  return (
    <>
      <EmailClientLinksContainer>
        <EmailClientLink
          onClick={trackEmailCTAClicked}
          href={generateGmailSniperLink()}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="email-confirmation-gmail-link"
          aria-label={translate(
            'email_confirmation.footer.email_client.gmail.a11y_label',
          )}
        >
          <OpenExternalLinkIcon />
          {translate('email_confirmation.footer.email_client.gmail.text')}
        </EmailClientLink>
        <EmailClientLink
          onClick={trackEmailCTAClicked}
          href="https://outlook.office.com/mail/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="email-confirmation-outlook-link"
          aria-label={translate(
            'email_confirmation.footer.email_client.outlook.a11y_label',
          )}
        >
          <OpenExternalLinkIcon />
          {translate('email_confirmation.footer.email_client.outlook.text')}
        </EmailClientLink>
      </EmailClientLinksContainer>
    </>
  );
};
